import Head from 'next/head';
import type { GetStaticPaths, GetStaticProps } from 'next';

import { sanityClient } from '@lib/sanity/client';
import { buildSanityImageUrl } from '@lib/sanity/image';
import {
  getAllLandingPageSlugsQuery,
  landingPageBySlugQuery
} from '@lib/sanity/queries';
import type { SanityLandingPage } from '@lib/sanity/types';
import LandingPageRenderer from '@components/landing/LandingPageRenderer';
import { FFH_HOSTNAME } from '@lib/constants';

// Time-based ISR: pages serve from the edge as static HTML and regenerate in
// the background at most this often, so traffic is fully decoupled from Sanity.
// A Sanity publish webhook can later swap this for on-demand revalidation.
const REVALIDATE_SECONDS = 60;
const OG_IMAGE_WIDTH = 1200;

// Without an ogImage of its own a landing page inherited _app's square brand
// logo, which X renders as a thumbnail and treats as a card worth little reach.
// This lands the money-back creative instead, sized 1200x675 for the
// full-width card. Absolute, because crawlers do not resolve relative URLs.
const DEFAULT_SHARE_IMAGE_URL = `${FFH_HOSTNAME}/images/money-back-promise-landscape.jpg`;

type LandingPageProps = {
  landingPage: SanityLandingPage;
  /** Read by _app to hide the site header/promo/footer on conversion pages. */
  hideSiteChrome: boolean;
};

const LandingPage = ({ landingPage }: LandingPageProps) => {
  const { title, seo, hero } = landingPage;

  const metaTitle = seo?.metaTitle || title;
  const metaDescription = seo?.metaDescription || hero?.subheading;
  const shareImageUrl = seo?.ogImage
    ? buildSanityImageUrl(seo.ogImage).width(OG_IMAGE_WIDTH).url()
    : DEFAULT_SHARE_IMAGE_URL;
  const isHiddenFromSearch = seo?.isHiddenFromSearch ?? false;

  return (
    <>
      <Head>
        <title>{`${metaTitle} | Fantasy Football Hub`}</title>
        {metaDescription && (
          <meta name="description" content={metaDescription} />
        )}
        {seo?.canonicalUrl && (
          <link rel="canonical" href={seo.canonicalUrl} key="canonical" />
        )}
        {isHiddenFromSearch && (
          <meta name="robots" content="noindex,nofollow" />
        )}
        <meta property="og:title" content={metaTitle} key="og:title" />
        {metaDescription && (
          <meta
            property="og:description"
            content={metaDescription}
            key="og:description"
          />
        )}
        {/* Keyed to match the _app default, which is the only way an og tag
            wins the next/head merge (the rules live in lib/seo.tsx). */}
        <meta property="og:image" content={shareImageUrl} key="og:image" />
        <meta
          name="twitter:card"
          content="summary_large_image"
          key="twitter:card"
        />
        <meta
          name="twitter:image"
          content={shareImageUrl}
          key="twitter:image"
        />
      </Head>
      <LandingPageRenderer landingPage={landingPage} />
    </>
  );
};

export const getStaticPaths: GetStaticPaths = async () => {
  const slugs = await sanityClient.fetch<{ slug: string }[]>(
    getAllLandingPageSlugsQuery()
  );

  return {
    paths: slugs
      .filter((entry) => entry.slug)
      .map((entry) => ({ params: { slug: entry.slug } })),
    fallback: 'blocking'
  };
};

export const getStaticProps: GetStaticProps<LandingPageProps> = async ({
  params
}) => {
  const slug = params?.slug as string;
  const landingPage = await sanityClient.fetch<SanityLandingPage | null>(
    landingPageBySlugQuery,
    { slug }
  );

  if (!landingPage) {
    return { notFound: true, revalidate: REVALIDATE_SECONDS };
  }

  return {
    props: {
      landingPage,
      // Conversion pages are chrome-light (no site nav/footer) to keep the
      // path to checkout free of exits, the same mechanism the quiz funnels
      // use.
      hideSiteChrome: true
    },
    revalidate: REVALIDATE_SECONDS
  };
};

export default LandingPage;
